// import React from 'react';
//
// import type { Identifier, XYCoord } from 'dnd-core'
// import cx from 'classnames';
// import { useRef } from 'react'
// import { useDrag, useDrop } from 'react-dnd'
//
// import { DraggableListDataItem, DraggableListDirection } from '../../draggable-list.types';
//
// import './item.styles.scss';
//
// export type ItemProps<T extends DraggableListDataItem = DraggableListDataItem> = {
//   className?: string;
//   data: T;
//   index: number;
//   direction: DraggableListDirection;
//   onDraggingStatusChange: (isDragging: boolean) => void;
//   renderContent: (item: T, index: number) => React.ReactNode;
//   moveCard: (dragIndex: number, hoverIndex: number) => void;
// };
//
//
// type DragItem = DraggableListDataItem & {
//   index: number;
//   type: string;
// };
//
// export function Item<T extends DraggableListDataItem = DraggableListDataItem>(props: ItemProps<T>) {
//   const { data, index, direction, renderContent, moveCard, className, onDraggingStatusChange } = props;
//   const ITEM_TYPE = 'AM_DRAGGABLE_LIST_ITEM_TYPE';
//
//   const content = React.useMemo(() => {
//     return renderContent(data, index);
//   }, [renderContent, data, index]);
//
//   const ref = useRef<HTMLDivElement>(null);
//   const [{ handlerId }, drop] = useDrop<
//     DragItem,
//     void,
//     { handlerId: Identifier | null }
//   >({
//     accept: ITEM_TYPE,
//     collect(monitor) {
//       return {
//         handlerId: monitor.getHandlerId(),
//       }
//     },
//     hover(item, monitor) {
//       if (!ref.current) {
//         return;
//       }
//
//       const dragIndex = item.index;
//       const hoverIndex = index;
//
//       // Don't replace items with themselves
//       if (dragIndex === hoverIndex) {
//         return;
//       }
//
//       // Determine rectangle on screen
//       const hoverBoundingRect = ref.current?.getBoundingClientRect()
//       // Determine mouse position
//       const clientOffset = monitor.getClientOffset();
//
//       if (direction === DraggableListDirection.vertical) {
//         // Get vertical middle
//         const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
//
//         // Get pixels to the top
//         const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;
//
//         // Only perform the move when the mouse has crossed half of the items height
//         // When dragging downwards, only move when the cursor is below 50%
//         // When dragging upwards, only move when the cursor is above 50%
//
//         // Dragging downwards
//         if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
//           return;
//         }
//
//         // Dragging upwards
//         if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
//           return;
//         }
//       }
//
//       if (direction === DraggableListDirection.horizontal) {
//         const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
//         const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRect.left;
//
//         if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
//           return;
//         }
//
//         if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
//           return;
//         }
//       }
//
//       // Time to actually perform the action
//       moveCard(dragIndex, hoverIndex);
//
//       // Note: we're mutating the monitor item here!
//       // Generally it's better to avoid mutations,
//       // but it's good here for the sake of performance
//       // to avoid expensive index searches.
//       item.index = hoverIndex;
//     },
//   });
//
//   const [{ isDragging }, drag] = useDrag({
//     type: ITEM_TYPE,
//     item: () => {
//       return { id: data.id, index };
//     },
//     collect: (monitor: any) => ({
//       isDragging: monitor.isDragging(),
//     }),
//   });
//
//
//   drag(drop(ref));
//
//   React.useEffect(() => {
//     onDraggingStatusChange(isDragging);
//   }, [isDragging]);
//
//   return (
//     <div
//       className={cx('DraggableListItem', { ['DraggableListItem--projection']: isDragging }, className)}
//       ref={ref}
//       data-handler-id={handlerId}
//     >
//       {content}
//     </div>
//   );
// };


import React, { useRef, useEffect, useMemo } from 'react';
import type { Identifier, XYCoord } from 'dnd-core';
import cx from 'classnames';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { DraggableListDataItem, DraggableListDirection } from '../../draggable-list.types';

import './item.styles.scss';

export type ItemProps<T extends DraggableListDataItem = DraggableListDataItem> = {
  className?: string;
  data: T;
  index: number;
  direction: DraggableListDirection;
  onDraggingStatusChange: (isDragging: boolean) => void;
  renderContent: (item: T, index: number) => React.ReactNode;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
};

type DragItem = DraggableListDataItem & {
  index: number;
  type: string;
};

export function Item<T extends DraggableListDataItem = DraggableListDataItem>(props: ItemProps<T>) {
  const { data, index, direction, renderContent, moveCard, className, onDraggingStatusChange } = props;
  const ITEM_TYPE = 'AM_DRAGGABLE_LIST_ITEM_TYPE';

  const content = useMemo(() => renderContent(data, index), [renderContent, data, index]);

  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ITEM_TYPE,
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
    }),
    hover(item, monitor) {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();

      if (direction === DraggableListDirection.vertical) {
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      }

      if (direction === DraggableListDirection.horizontal) {
        const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
        const hoverClientX = (clientOffset as XYCoord).x - hoverBoundingRect.left;

        if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) return;
        if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) return;
      }

      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: ITEM_TYPE,
    item: () => ({ id: data.id, index }),
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    onDraggingStatusChange(isDragging);
  }, [isDragging, onDraggingStatusChange]);

  // Hide the default ghost preview from the browser
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  drag(drop(ref));

  return (
      <div
          className={cx(
              'DraggableListItem',
              { 'DraggableListItem--dragging': isDragging },
              className
          )}
          ref={ref}
          data-handler-id={handlerId}
      >
        {content}
      </div>
  );
}